import * as React from "react";
import { useEffect } from "react";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../redux/global/actions";
import { exportMaterialContractsToCSV } from "../redux/material/actions";
import {
  getPlaceholderMaterialDetails,
  getPlaceholderMaterials,
  getPlaceholderMaterialsBulk,
} from "../redux/material_placeholder/actions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import MultiValueInput from "../inputs/multiValueInput";
import {
  Box,
  Grid,
  Stack,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchDateInput from "../inputs/searchDateInput";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../utils";
import SingleSelector from "../inputs/singleSelector";
import MultiSelector from "../inputs/MultiSelector";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../widgets/ExportMenu";

const PLACEHOLDER_MATERIAL_COLUMNS = [
  {
    id: "serial_number",
    label: "Serial Number",
    filter: "text",
    nowrap: true,
  },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "part_number", label: "Part Number", filter: "text" },
  { id: "sold_to", label: "Sold To", filter: "text" },
  {
    id: "business_partner_company_id",
    label: "Business Partner",
    filter: "text",
  },
  { id: "order_id", label: "Order Id", filter: "text" },
  { id: "order_line", label: "Order Line", filter: "text" },
  { id: "is_naas", label: "Is NaaS", filter: "select" },
];

export default function PlaceholderMaterials() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessedUrlParams = React.useRef(false);

  const queryParameters = useSelector(
    (state) => state?.parameters?.placeholderMaterials,
  );

  const [currentSerialNumbers, setCurrentSerialNumbers] = React.useState();
  const [currentProductReferences, setCurrentProductReferences] =
    React.useState();
  const [currentSoldTo, setCurrentSoldTo] = React.useState();
  const [currentBusinessPartnerId, setCurrentBusinessPartnerId] =
    React.useState();
  const [currentEndUser, setCurrentEndUser] = React.useState();
  const [currentPartNumber, setCurrentPartNumber] = React.useState();

  const [selectedTab, setSelectedTab] = React.useState(0);

  const tabs = [
    {
      label: "Placeholder Materials",
      filters: [
        "serialNumbers",
        "productReferences",
        "soldTos",
        "businessPartnerIds",
        "endUser",
        "partNumber",
        "isNaaS",
        "creationDate",
      ],
    },
    {
      label: "Monitoring Material Swap",
      filters: ["swapStatus"],
    },
  ];

  const handleExecuteSearch = () => {
    let queryObject = {
      limit: 100,
    };
    const currentFilters = tabs[selectedTab].filters;
    if (
      currentFilters.includes("serialNumbers") &&
      queryParameters?.serialNumbers
    ) {
      queryObject.serial_number = queryParameters?.serialNumbers;
    }
    if (
      currentFilters.includes("productReferences") &&
      queryParameters?.productReferences
    ) {
      queryObject.bundle_reference = queryParameters?.productReferences;
    }
    if (currentFilters.includes("soldTos") && queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (
      currentFilters.includes("businessPartnerIds") &&
      queryParameters?.businessPartnerIds
    ) {
      queryObject.business_partner = queryParameters?.businessPartnerIds;
    }
    if (currentFilters.includes("endUser") && queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (currentFilters.includes("partNumber") && queryParameters?.partNumber) {
      queryObject.part_number = queryParameters?.partNumber;
    }
    if (currentFilters.includes("swapStatus") && queryParameters?.swapStatus) {
      queryObject.swap_status = queryParameters?.swapStatus;
    }
    if (
      currentFilters.includes("creationDate") &&
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    dispatch(getPlaceholderMaterials(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    const currentFilters = tabs[selectedTab].filters;
    if (
      currentFilters.includes("serialNumbers") &&
      queryParameters?.serialNumbers
    ) {
      queryObject.serial_number = queryParameters?.serialNumbers;
    }
    if (
      currentFilters.includes("productReferences") &&
      queryParameters?.productReferences
    ) {
      queryObject.bundle_reference = queryParameters?.productReferences;
    }
    if (currentFilters.includes("soldTos") && queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (
      currentFilters.includes("businessPartnerIds") &&
      queryParameters?.businessPartnerIds
    ) {
      queryObject.business_partner_id = queryParameters?.businessPartnerIds;
    }
    if (currentFilters.includes("endUser") && queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (currentFilters.includes("isNaaS") && queryParameters?.isNaaS) {
      queryObject.is_naas = queryParameters?.isNaaS;
    }
    if (currentFilters.includes("swapStatus") && queryParameters?.swapStatus) {
      queryObject.swap_status = queryParameters?.swapStatus;
    }
    if (
      currentFilters.includes("creationDate") &&
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getPlaceholderMaterialsBulk(queryObject));
  };

  // Reset the URL processing flag when the URL actually changes
  useEffect(() => {
    hasProcessedUrlParams.current = false;
  }, [searchParams.toString()]);

  useEffect(() => {
    if (hasProcessedUrlParams.current) return;

    const businessPartnerId = searchParams.get("businessPartnerId");
    if (businessPartnerId) {
      const businessPartnerIds = businessPartnerId
        .split(",")
        .map((id) => id.trim());

      // Check if these business partner IDs are already in the Redux state
      const existingBusinessPartnerIds =
        queryParameters?.businessPartnerIds || [];
      const newBusinessPartnerIds = businessPartnerIds.filter(
        (id) => !existingBusinessPartnerIds.includes(id),
      );

      if (newBusinessPartnerIds.length > 0) {
        setCurrentBusinessPartnerId(businessPartnerIds.join(", "));
        dispatch(
          setParameters({
            placeholderMaterials: { businessPartnerIds: newBusinessPartnerIds },
          }),
        );
        hasProcessedUrlParams.current = true;

        setTimeout(() => {
          handleExecuteSearch();
        }, 100);
      } else {
        // If business partner IDs already exist, just update the display
        setCurrentBusinessPartnerId(existingBusinessPartnerIds.join(", "));
        hasProcessedUrlParams.current = true;
      }
    }
  }, [searchParams, dispatch, queryParameters]);

  const handleDeleteSerialNumber = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        placeholderMaterials: { serialNumbers: [chipToDelete] },
      }),
    );
  };
  const handleClearSerialNumbers = () => {
    dispatch(clearParameters({ placeholderMaterials: "serialNumbers" }));
  };
  const handleDeleteProductReference = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        placeholderMaterials: { productReferences: [chipToDelete] },
      }),
    );
  };
  const handleClearProductReferences = () => {
    dispatch(clearParameters({ placeholderMaterials: "productReferences" }));
  };
  const handleDeleteSoldTo = (chipToDelete) => () => {
    dispatch(
      removeParameter({ placeholderMaterials: { soldTos: [chipToDelete] } }),
    );
  };
  const handleClearSoldTos = () => {
    dispatch(clearParameters({ placeholderMaterials: "soldTos" }));
  };
  const handleDeleteBusinessPartnerId = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        placeholderMaterials: { businessPartnerIds: [chipToDelete] },
      }),
    );
  };
  const handleClearBusinessPartnerIds = () => {
    dispatch(clearParameters({ placeholderMaterials: "businessPartnerIds" }));
  };
  const handleDeleteEndUser = (chipToDelete) => () => {
    dispatch(
      removeParameter({ placeholderMaterials: { endUser: [chipToDelete] } }),
    );
  };
  const handleClearEndUser = () => {
    dispatch(clearParameters({ placeholderMaterials: "endUser" }));
  };
  const handleDeletePartNumber = (chipToDelete) => () => {
    dispatch(
      removeParameter({ placeholderMaterials: { partNumber: [chipToDelete] } }),
    );
  };
  const handleClearPartNumber = () => {
    dispatch(clearParameters({ placeholderMaterials: "partNumber" }));
  };
  const handleDeleteIsNaaS = (chipToDelete) => () => {
    dispatch(
      removeParameter({ placeholderMaterials: { isNaaS: [chipToDelete] } }),
    );
  };
  const handleClearIsNaaS = () => {
    dispatch(clearParameters({ placeholderMaterials: "isNaaS" }));
  };
  const handleDeleteSwapStatus = (chipToDelete) => () => {
    dispatch(
      removeParameter({ placeholderMaterials: { swapStatus: [chipToDelete] } }),
    );
  };
  const handleClearSwapStatus = () => {
    dispatch(clearParameters({ placeholderMaterials: "swapStatus" }));
  };
  const handleExport = (materials, format = "csv") => {
    const fields = [
      { name: "serial_number" },
      { name: "status" },
      { name: "inactive_reason" },
      { name: "order_id" },
      { name: "part_number" },
      { name: "business_partner", value: "business_partner_company_id" },
      { name: "support_partner", value: "support_partner_company_id" },
      { name: "end_user", value: "end_user_company_id" },
      { name: "children" },
    ];
    if (format === "csv") {
      downloadCSV(fields, "material_export", ",", materials);
    } else {
      downloadJSON(fields, "material_export", materials);
    }
  };

  const handleExportContractsToCSV = (materials) => {
    dispatch(exportMaterialContractsToCSV(materials, "placeholder"));
  };

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const placeholderMaterials = useSelector((state) => {
    if (state.filteredPlaceholderMaterials) {
      return state.filteredPlaceholderMaterials.map((serialNumber) => {
        return {
          modification_date: serialNumber.modification_date,
          part_number: serialNumber.part_number,
          inactive_reason: serialNumber.inactive_reason,
          status: serialNumber.status,
          creation_note: serialNumber.creation_note,
          comments: serialNumber.comments,
          serial_number: serialNumber.serial_number,
          sn_type: serialNumber.sn_type,
          description: serialNumber.description,
          source_time: serialNumber.source_time,
          type: serialNumber.type,
          creation_date: serialNumber.creation_date,
          sold_to: serialNumber?.sold_to?.applicant_id,
          order_line: serialNumber?.order?.order_line,
          order_id: serialNumber?.order?.order_id,
          purchase_order: serialNumber?.order?.purchase_order,
          end_user_source: serialNumber?.end_user?.source,
          end_user_company_id: serialNumber?.end_user?.company_id,
          business_partner_source: serialNumber?.business_partner?.source,
          business_partner_company_id:
            serialNumber?.business_partner?.company_id,
          support_partner_source: serialNumber?.support_partner?.source,
          support_partner_company_id: serialNumber?.support_partner?.company_id,
          is_naas: serialNumber?.is_naas,
          placeholder_id: serialNumber?.placeholder_id,
        };
      });
    } else return [];
  });

  function handleSelectPlaceholderMaterial(placeholder_id, orderId, orderLine) {
    let parts = placeholder_id.split("#");

    let serial_number = parts[0];
    let business_partner = parts[1];
    dispatch(
      getPlaceholderMaterialDetails({
        serial_number: serial_number,
        business_partner: business_partner,
        order_id: orderId,
        order_line: orderLine,
      }),
    );
    navigate(
      "/placeholdermaterials/details/" + serial_number + "/" + business_partner,
    );
  }

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  const isNaaSValue = ["True", "False"];
  const swapStatusValues = ["Replaced - HWS In"];

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenBulkMessage(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Tabs
        value={selectedTab}
        onChange={(e, newValue) => setSelectedTab(newValue)}
        aria-label="filter tabs"
      >
        {tabs.map((tab, index) => (
          <Tab label={tab.label} key={index} />
        ))}
      </Tabs>

      <div role="tabpanel" hidden={selectedTab !== 0}>
        {selectedTab === 0 && (
          <Box sx={{ p: 3 }}>
            <MultiValueInput
              setCurrentParam={setCurrentSerialNumbers}
              setQueryParams={setParameters}
              currentParam={currentSerialNumbers}
              queryParams={queryParameters?.serialNumbers}
              handleDelete={handleDeleteSerialNumber}
              handleClear={handleClearSerialNumbers}
              label="Serial Number"
              family="placeholderMaterials"
              type="serialNumbers"
            />
            <MultiValueInput
              setCurrentParam={setCurrentProductReferences}
              setQueryParams={setParameters}
              currentParam={currentProductReferences}
              queryParams={queryParameters?.productReferences}
              handleDelete={handleDeleteProductReference}
              handleClear={handleClearProductReferences}
              label="Product Reference"
              family="placeholderMaterials"
              type="productReferences"
            />
            <MultiValueInput
              setCurrentParam={setCurrentSoldTo}
              setQueryParams={setParameters}
              currentParam={currentSoldTo}
              queryParams={queryParameters?.soldTos}
              handleDelete={handleDeleteSoldTo}
              handleClear={handleClearSoldTos}
              label="Sold To"
              family="placeholderMaterials"
              type="soldTos"
            />
            <MultiValueInput
              setCurrentParam={setCurrentBusinessPartnerId}
              setQueryParams={setParameters}
              currentParam={currentBusinessPartnerId}
              queryParams={queryParameters?.businessPartnerIds}
              handleDelete={handleDeleteBusinessPartnerId}
              handleClear={handleClearBusinessPartnerIds}
              label="Business Partner ID"
              family="placeholderMaterials"
              type="businessPartnerIds"
            />
            <MultiValueInput
              setCurrentParam={setCurrentEndUser}
              setQueryParams={setParameters}
              currentParam={currentEndUser}
              queryParams={queryParameters?.endUser}
              handleDelete={handleDeleteEndUser}
              handleClear={handleClearEndUser}
              label="End User"
              family="placeholderMaterials"
              type="endUser"
            />
            <MultiValueInput
              setCurrentParam={setCurrentPartNumber}
              setQueryParams={setParameters}
              currentParam={currentPartNumber}
              queryParams={queryParameters?.partNumber}
              handleDelete={handleDeletePartNumber}
              handleClear={handleClearPartNumber}
              label="Part Number"
              family="placeholderMaterials"
              type="partNumber"
            />
            <SingleSelector
              setQueryParams={setParameters}
              queryParams={queryParameters?.isNaaS}
              handleDelete={handleDeleteIsNaaS}
              handleClear={handleClearIsNaaS}
              label="Is NaaS"
              family="placeholderMaterials"
              type="isNaaS"
              options={isNaaSValue}
            />
            <Grid>
              <SearchDateInput
                title={"Creation Date"}
                setFilterDate={setDateParameters}
                family="placeholderMaterials"
                type="creationDateStart"
                type2="creationDateEnd"
              />
            </Grid>
          </Box>
        )}
      </div>

      <div role="tabpanel" hidden={selectedTab !== 1}>
        {selectedTab === 1 && (
          <Box sx={{ p: 3 }}>
            <MultiSelector
              setQueryParams={setParameters}
              queryParams={queryParameters?.swapStatus}
              handleDelete={handleDeleteSwapStatus}
              handleClear={handleClearSwapStatus}
              label="Swap Status"
              family="placeholderMaterials"
              type="swapStatus"
              options={swapStatusValues}
            />
          </Box>
        )}
      </div>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        {selectedTab === 0 && (
          <Button
            variant="outlined"
            startIcon={<MaterialSymbol name="cloud_download" size="button" />}
            onClick={() => setOpenBulkConfirmation(true)}
          >
            Bulk Export
          </Button>
        )}
      </Stack>
      {placeholderMaterials.length > 0 && (
        <DataTable
          title="Materials"
          subtitle={
            placeholderMaterials.length === 100
              ? `${placeholderMaterials.length} results, use bulk export for more`
              : `${placeholderMaterials.length} results`
          }
          columns={PLACEHOLDER_MATERIAL_COLUMNS}
          rows={placeholderMaterials}
          getRowId={(row, i) => `${row?.placeholder_id}-${i}`}
          onRowClick={(row) =>
            handleSelectPlaceholderMaterial(
              row?.placeholder_id,
              row?.order_id,
              row?.order_line,
            )
          }
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <>
              <ExportMenu
                disabled={!rows.length}
                onExport={(format) => handleExport(rows, format)}
              />
              <Button
                variant="outlined"
                startIcon={<MaterialSymbol name="description" size="button" />}
                disabled={!rows.length}
                onClick={() => handleExportContractsToCSV(rows)}
              >
                Export contracts
              </Button>
            </>
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
